import { isObject, has, get, isArray } from "lodash-es";
import ExpectedProp from "../../util/expected-prop";
import { ModelType } from "../global-types/customization-types";
import { LabelJsonWrapper } from "../../web-apis/backend-api-interface";

export type Labeling = {
    /** The model type that this labeling is for */
    modelType: ModelType,

    udi: string | null,
    manufacturer: string,
    manufacturerModelName: string,
    isClinical: boolean,
    systemVersion: string,
    description: string,
    labelingObject: unknown,
    labelingAsString: string,
}

export type CombinedLabeling = {
    labels: Labeling[],
    backendVersionAsString: string | undefined,
}

export type WorkspaceLabeling = {
    isClinical: boolean,
    description: string,
    manufactureDate: string,
    manufacturer: string,
    manufacturerModelName: string,
    systemVersion: string,
    udi: string, 
    labelingObject: unknown,
    labelingAsString: string,
}

export const createNewLabeling = (
    modelType: ModelType,
    udi: string | null,
    manufacturer: string,
    manufacturerModelName: string,
    isClinical: boolean,
    systemVersion: string,
    description: string,
    labelingObject: object,
): Labeling => {
    return {
        modelType: modelType,
        udi: udi,
        manufacturer: manufacturer,
        manufacturerModelName: manufacturerModelName,
        isClinical: isClinical,
        systemVersion: systemVersion,
        description: description,
        labelingObject: labelingObject,
        labelingAsString: Object.entries(labelingObject).map(([key, value]) => `${key}: ${value}`).join('\n'),
    };
}

export const createNewWorkspaceLabeling = (
    isClinical: boolean,
    description: string,
    manufactureDate: string,
    manufacturer: string,
    manufacturerModelName: string,
    systemVersion: string,
    udi: string,
    labelingObject: object,
): WorkspaceLabeling => {
    return {
        isClinical: isClinical,
        description: description,
        manufactureDate: manufactureDate,
        manufacturer: manufacturer,
        manufacturerModelName: manufacturerModelName,
        systemVersion: systemVersion,
        udi: udi,
        labelingObject: labelingObject,
        labelingAsString: Object.entries(labelingObject).map(([key, value]) => `${key}: ${value}`).join('\n'),
    }
}


/** Parses backend version JSON object into a user-presentable(-ish) string */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const parseBackendVersion = (backendVersionJson: any): string | undefined => {
    if (!backendVersionJson) {
        return undefined;
    }

    const labels: string[] = [];

    for (const key of Object.keys(backendVersionJson)) {
        const value = backendVersionJson[key];
        if (isArray(value) && key.includes('_plus')) {
            // parse this as an array of worker version objects
            const workers: string[] = [];

            for (const worker of value) {
                // build_date and git_hash are the only supported worker version fields as of now
                const workerInfo: string[] = [];
                for (const supportedJsonField of ['build_date', 'git_hash']) {
                    if (get(worker, supportedJsonField, undefined)) { workerInfo.push(`${supportedJsonField}: ${worker[supportedJsonField]}`); }
                }
                workers.push(workerInfo.join(', '));
            }
            if (workers.length) {
                labels.push(`${key}: [{ ${workers.join(' }, { ')} }]`);
            } else {
                labels.push(`${key}: N/A`);
            }

        } else {
            // show any non-array field
            labels.push(`${key}: ${value}`);
        }
    }

    return labels.join('\n');
}


const expectedLabelingProps = [
    new ExpectedProp('manufacturer', 'string'),
    new ExpectedProp('manufacturer_model_name', 'string'),
    new ExpectedProp('clinical', 'boolean'),
    new ExpectedProp('system_version', 'string'),
    new ExpectedProp('description', 'string'),
];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const convertJsonObjectToLabeling = (labelingJsonObjects: LabelJsonWrapper[], backendVersion?: any): CombinedLabeling => {
    if (labelingJsonObjects.length === 0 || !labelingJsonObjects.every(j => isObject(j.labeling))) {
        throw new Error('Invalid JSON object provided for Labeling conversion.');
    }

    const labels: Labeling[] = [];

    for (const labelingJsonObject of labelingJsonObjects) {

        const json = labelingJsonObject.labeling;

        const notFoundProps: ExpectedProp[] = [];
        for (const expectedProp of expectedLabelingProps) {
            if (!has(json, expectedProp.jsonProp) || typeof json[expectedProp.jsonProp] !== expectedProp.type) {
                console.warn(`Expected prop ${expectedProp.jsonProp} of type ${expectedProp.type}, but got ${typeof json[expectedProp.jsonProp]}.`);	
                notFoundProps.push(expectedProp);
            }
        }

        if (notFoundProps.length > 0) {
            const msg = `Expected to find following props from Labeling JSON object, but didn't: ${notFoundProps.map(p => p.jsonProp).join(', ')}.`;
            console.error(msg + ' JSON:');
            console.error(json);
            throw new Error(msg);
        }

        labels.push(createNewLabeling(
            labelingJsonObject.modelType,
            get(json, 'udi', null),
            get(json, 'manufacturer', ''),
            get(json, 'manufacturer_model_name', ''),
            get(json, 'clinical', false),
            get(json, 'system_version', ''),
            get(json, 'description', ''),
            json,
        ));
    }

    return { labels, backendVersionAsString: parseBackendVersion(backendVersion) };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const convertJsonObjectToWorkspaceLabeling = (workspaceJson: any): WorkspaceLabeling => {
    if (!isObject(workspaceJson)) {
        throw new Error('Invalid JSON object provided for Workspace Labeling conversion.');
    }

    const labeling = workspaceJson;

    return createNewWorkspaceLabeling(
        get(workspaceJson, 'clinical', false),
        get(labeling, 'description', ''),
        get(labeling, 'manufacture_date', ''),
        get(labeling, 'manufacturer', ''),
        get(labeling, 'manufacturer_model_name', ''),
        get(labeling, 'system_version', ''),
        get(labeling, 'udi', ''),
        labeling,
    );
}